/** @jsxImportSource @emotion/react */

import { useMemo } from 'react'
import Typography from '@mui/material/Typography'
import { useFormState } from 'react-final-form'
import { sortBy } from 'lodash'
import { FormValues } from 'pagesNew/StudySchedule/Schedule/consts'
import { findAllForCMSTask_findAllForCMSTask_items as Task } from '__generatedTypes__/findAllForCMSTask'
import { COURSE_COLORS, CourseName } from 'theme/utils'
import { ReactComponent as EmptyListSvg } from 'components/QuestionAccordion/images/empty.svg'
import { TaskTypeEnum } from '__generatedTypes__/globalTypes'
import { PHASES } from '../ScheduleOrder/consts'
import { TASK_TYPE_TITLE } from '../consts'
import * as classes from './styles'
import { ChartCardContent } from './ChartCardContent'

const TASK_TYPE_COLORS: Record<TaskTypeEnum, string> = {
  [TaskTypeEnum.PracticeTest]: '#403F3F',
  [TaskTypeEnum.Video]: '#585858',
  [TaskTypeEnum.QBank]: '#838383',
  [TaskTypeEnum.Resource]: '#ABABAB',
  [TaskTypeEnum.Custom]: '#FFFFFF',
}

const TASK_SUBJECT_ORDERS: Record<CourseName, number> = Object.keys(COURSE_COLORS).reduce(
  (acc, courseName, index) => ({
    ...acc,
    [courseName]: index,
  }),
  {} as Record<CourseName, number>
)

const TASK_TYPE_ORDERS: Record<TaskTypeEnum, number> = {
  [TaskTypeEnum.PracticeTest]: 1,
  [TaskTypeEnum.Video]: 2,
  [TaskTypeEnum.QBank]: 3,
  [TaskTypeEnum.Resource]: 4,
  [TaskTypeEnum.Custom]: 5,
}

type DatasetItem = {
  order: number
  totalTime: number
  tasks: Task[]
}

export const ScheduleAnalytics = () => {
  const { values } = useFormState<FormValues>()

  const { subjectDatasets, typeDatasets, totalTime } = useMemo(() => {
    const allTasks = Object.values(PHASES).reduce((acc, phase) => {
      const value = values[phase] as Task[]

      return [...acc, ...(value || [])]
    }, [] as Task[])

    let totalTime = 0

    const subjectDatasets = allTasks.reduce((acc, task) => {
      const subjectId = task.subject?.id

      totalTime += task.time || 0

      if (!subjectId) {
        return acc
      }

      if (!acc[subjectId]) {
        acc[subjectId] = {
          order: TASK_SUBJECT_ORDERS[task.subject?.tabName as CourseName] || -1,
          totalTime: 0,
          tasks: [],
        }
      }

      acc[subjectId].totalTime += task.time || 0
      acc[subjectId].tasks.push(task)

      return acc
    }, {} as Record<string, DatasetItem>)

    const typeDatasets = allTasks.reduce((acc, task) => {
      const type = task.type

      if (!acc[type]) {
        acc[type] = {
          order: TASK_TYPE_ORDERS[type],
          totalTime: 0,
          tasks: [],
        }
      }

      acc[type].totalTime += task.time || 0
      acc[type].tasks.push(task)

      return acc
    }, {} as Record<string, DatasetItem>)

    return {
      subjectDatasets: sortBy(Object.values(subjectDatasets), 'order'),
      typeDatasets: sortBy(Object.values(typeDatasets), 'order'),
      totalTime,
    }
  }, [values])

  const { subjectsChartProps, typesChartProps } = useMemo(
    () => ({
      subjectsChartProps: {
        datasets: [
          {
            data: subjectDatasets.map((subject) => subject.totalTime),
            backgroundColor: subjectDatasets.map(
              (subject) => COURSE_COLORS[subject.tasks[0].subject?.tabName as CourseName].main
            ),
          },
        ],
        listData: subjectDatasets.map((subject) => ({
          name: subject.tasks[0].subject?.tabName || '',
          color: COURSE_COLORS[subject.tasks[0].subject?.tabName as CourseName].main,
          time: subject.totalTime,
        })),
      },
      typesChartProps: {
        datasets: [
          {
            data: typeDatasets.map((type) => type.totalTime),
            backgroundColor: typeDatasets.map((type) => TASK_TYPE_COLORS[type.tasks[0].type]),
          },
        ],
        listData: typeDatasets.map((type) => ({
          name: TASK_TYPE_TITLE[type.tasks[0].type],
          color: TASK_TYPE_COLORS[type.tasks[0].type],
          time: type.totalTime,
        })),
      },
    }),
    [subjectDatasets, typeDatasets]
  )

  return (
    <div>
      <Typography variant="h3" css={classes.title}>
        Schedule Analytics
      </Typography>

      <div css={classes.charts}>
        <div css={classes.chartCard} data-testid="subjects-chart">
          {subjectDatasets.length ? (
            <ChartCardContent {...subjectsChartProps} totalTime={totalTime} />
          ) : (
            <div css={classes.emptyCard}>
              <EmptyListSvg />
              <div>No tasks at the moment</div>
            </div>
          )}
        </div>

        <div css={classes.chartCard} data-testid="types-chart">
          {typeDatasets.length ? (
            <ChartCardContent {...typesChartProps} totalTime={totalTime} />
          ) : (
            <div css={classes.emptyCard}>
              <EmptyListSvg />
              <div>No tasks at the moment</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
